// Write and send an email to a store without leaving the tab. Prefilled
// from the step's script (subject on the first line), sent from the rep's
// own address through /api/sales-email, and kept on the store's thread.
// "Make it about them" keeps the script and adds one or two lines from
// the brief; the rep sees exactly which sentences were added. When
// sending is not set up on the server, the same button opens the Mail
// app instead, so nothing is blocked.
import React, { useEffect, useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, Mail, RefreshCw, Send, Sparkles } from 'lucide-react';
import { assist, prospectForAssist } from './salesAssist';
import { emailStatus, sendEmail } from './salesData';
import { hasUnfilled } from './salesPlan';
import { Field, btnGhost, btnPrimary, btnSmall, inputCls } from './salesUi';

/** Split "Subject: X\n\nbody" into { subject, body }. */
export const splitScript = (text) => {
  const m = String(text || '').match(/^Subject:\s*(.+)\n+([\s\S]*)$/);
  return m ? { subject: m[1].trim(), body: m[2].trim() } : { subject: '', body: String(text || '').trim() };
};

let statusCache = null; // one GET per page load is plenty

export default function EmailComposer({ client, storeId, prospect: p, me, initialTo = '', initialSubject = '', initialBody = '', context = '', onSent, onClose, notify, compact = false }) {
  const [to, setTo] = useState(initialTo || p.email || '');
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [status, setStatus] = useState(statusCache);
  const [busy, setBusy] = useState(false);
  const [personalizing, setPersonalizing] = useState(false);
  const [changed, setChanged] = useState([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    if (statusCache) return undefined;
    emailStatus(client).then((s) => { statusCache = s; if (alive) setStatus(s); });
    return () => { alive = false; };
  }, [client]);
  useEffect(() => { setSubject(initialSubject); setBody(initialBody); setChanged([]); }, [p.id, initialSubject, initialBody]);

  const configured = !!status?.configured;
  const repEmail = me?.email || '';
  const repOk = configured && repEmail && (!status?.domain || repEmail.toLowerCase().endsWith(`@${String(status.domain).toLowerCase()}`));
  // A script with [video link] or another unfilled placeholder never goes out.
  const unfilled = hasUnfilled(subject) || hasUnfilled(body);
  const canSend = repOk && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to) && subject.trim() && body.trim() && !unfilled;
  const mailto = useMemo(() => `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, [to, subject, body]);

  const personalize = async () => {
    if (personalizing) return;
    setPersonalizing(true);
    setError('');
    try {
      const res = await assist(client, storeId, 'email', {
        script: { subject, body },
        prospect: prospectForAssist(p),
        brief_facts: p.brief?.facts ? Object.fromEntries(Object.entries(p.brief.facts).filter(([, v]) => v?.value).map(([k, v]) => [k, v.value])) : {},
        context,
        rep_name: me?.name || '',
      });
      if (res.subject) setSubject(res.subject);
      if (res.body) setBody(res.body);
      setChanged(res.changed || []);
      if (!(res.changed || []).length) notify('Nothing on file about this store yet. Build the brief first and the email picks up a detail from it.');
    } catch (e) {
      console.error('sales: email personalize failed', e);
      setError(e.message || 'Could not personalize');
    } finally {
      setPersonalizing(false);
    }
  };

  const send = async () => {
    if (!canSend || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await sendEmail(client, { store_id: storeId, prospect_id: p.id, rep_id: me.id, to: to.trim(), subject: subject.trim(), body: body.trim() });
      notify(`Sent to ${to.trim()}. Replies land on this store and in your inbox.`);
      onSent?.({ subject: subject.trim(), to: to.trim(), id: res.id, row: res.row });
    } catch (e) {
      console.error('sales: send failed', e);
      setError(e.message || 'Could not send');
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  return (
    <div className={`rounded-2xl border border-sky-200 bg-sky-50/40 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-sky-700" />
        <span className="text-sm font-bold text-slate-900">Email {p.name}</span>
        {repOk ? <span className="text-xs text-slate-500 truncate">from {repEmail}</span> : configured ? <span className="text-xs text-amber-700">Set your @{status?.domain} email on the Team panel to send from here</span> : status ? <span className="text-xs text-slate-500">Sending from the app is not set up yet. This opens your Mail app.</span> : null}
        {onClose && <button type="button" onClick={onClose} className="ml-auto text-xs font-semibold text-slate-500 hover:text-slate-800">Close</button>}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2">
        <Field label="To"><input type="email" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} placeholder="owner@store.com" /></Field>
        <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} maxLength={120} /></Field>
      </div>
      <div className="mt-2">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={compact ? 7 : 10} className={`${inputCls} font-sans`} />
      </div>
      {changed.length > 0 && (
        <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
          <span className="font-semibold">Added for this store, check them:</span>
          <ul className="mt-1 list-disc pl-4 space-y-0.5">{changed.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      {unfilled && <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{/\[video link\]/i.test(body) ? 'The demo video link is not in yet. Paste it over [video link], or wait until it is set for everyone.' : 'Something in brackets is still unfilled. Replace it before sending.'}</div>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={personalize} disabled={personalizing || !body.trim()} className={btnGhost} title="Keeps the script, adds one or two lines from the brief">
          <Sparkles className={`w-4 h-4 text-violet-600 ${personalizing ? 'animate-pulse' : ''}`} /> {personalizing ? 'Reading the brief' : 'Make it about them'}
        </button>
        <button type="button" onClick={copyAll} className={btnSmall}>{copied ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}</button>
        <span className="ml-auto flex items-center gap-2">
          {repOk ? (
            <button type="button" onClick={send} disabled={!canSend || busy} className={btnPrimary}>{busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send</button>
          ) : (
            <a href={unfilled ? undefined : mailto} aria-disabled={unfilled} className={`${btnPrimary} ${unfilled ? 'pointer-events-none opacity-50' : ''}`} onClick={() => !unfilled && onSent?.({ subject, to, external: true })}><ExternalLink className="w-4 h-4" /> Open in Mail</a>
          )}
        </span>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Sent as plain text from you, with a one-line footer (company, address, how to stop). Never email a store that said no.</p>
    </div>
  );
}

/** The thread on a store: what we sent, what they wrote back. */
export function EmailThread({ emails, onReply }) {
  if (!emails?.length) return null;
  const when = (iso) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <div className="space-y-2">
      {emails.map((m) => (
        <div key={m.id} className={`rounded-xl border p-3 ${m.direction === 'in' ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'}`}>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className={`font-bold uppercase tracking-wider ${m.direction === 'in' ? 'text-emerald-700' : 'text-slate-500'}`}>{m.direction === 'in' ? 'They wrote' : 'We sent'}</span>
            <span className="text-slate-400">{when(m.happened_at)}</span>
            <span className="text-slate-400 truncate">{m.direction === 'in' ? m.from_addr : `to ${m.to_addr}`}</span>
            {m.status && m.status !== 'sent' && m.status !== 'received' && <span className={`ml-auto px-1.5 py-0.5 rounded-full border font-bold ${m.status === 'delivered' ? 'border-emerald-200 text-emerald-700' : 'border-red-200 text-red-700'}`}>{m.status}</span>}
          </div>
          {m.subject && <div className="text-sm font-semibold text-slate-900 mt-1">{m.subject}</div>}
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 mt-1 max-h-48 overflow-y-auto">{m.body_text}</pre>
          {m.direction === 'in' && onReply && <button type="button" onClick={() => onReply(m)} className="mt-2 text-xs font-semibold text-violet-700 hover:underline">Draft a reply to this</button>}
        </div>
      ))}
    </div>
  );
}
